import { ExtendSubdomainService } from 'shared/models/service';
import authenticationRedirectHandler from 'modules/core/auth/authenticationRedirectHandler';
import { getToken } from 'modules/core/auth/token';
import DevsiteCookies, { getCookie } from '../DevsiteCookies';
import { HeaderKeys, HttpClientResponse, MimeType, QueryParameters, RequestHeaders } from './Definitions';
import { RegionalSubdomainExecutor } from './RegionalSubdomainExecutor';
import { authenticationInterceptor, dtsAuthenticationInterceptor } from './interceptors';

// Standalone devtools-server request executor with version prefix added. Will be default builder executor once migrations are fully completed.
class DevToolsServerRequestExecutor<T extends string> extends RegionalSubdomainExecutor<T> {
  protected execute(
    url: string,
    params: Partial<RequestInit>,
    headers?: RequestHeaders,
    queryParams?: QueryParameters
  ): Promise<HttpClientResponse> {
    return super.execute(
      url,
      params,
      this.getDevToolsHeaders(headers),
      queryParams,
      { credentials: 'include' } // include this so that devsite cookies are sent to devtools-server
    );
  }

  // Add X-XSRF-TOKEN header, based off the AB-XSRF-TOKEN cookie, for csrf security for builder server.
  // These identifiers are unique to Spring sessions & web security and is currently specific to App Builder.
  getDevToolsHeaders(headers?: RequestHeaders): RequestHeaders {
    const combinedHeaders = { [HeaderKeys.ORG_ID]: getCookie(DevsiteCookies.WCP_ORG_UUID), ...headers };
    const xsrfToken = getCookie(DevsiteCookies.AB_XSRF_TOKEN);
    if (xsrfToken) {
      combinedHeaders[HeaderKeys.X_XSRF_TOKEN] = xsrfToken;
    }

    return combinedHeaders;
  }

  /**
   * Makes a POST request and returns the raw streaming Response for SSE consumption.
   * Adds the standard DevTools auth and CSRF headers, and the Bearer token required
   * for streaming endpoints.
   * Auth errors (401/403) trigger authenticationRedirectHandler before the Response is returned,
   * matching the behaviour of the interceptor path. Other error status codes are returned as-is.
   * @deprecated just use post with signal instead. Request Executor was updated to support streaming.
   */
  async postStream(url: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    const fullUrl = await this.fullUrlAsync(url as T);
    const token = getToken();
    const headers = this.getDevToolsHeaders({
      [HeaderKeys.CONTENT_TYPE]: MimeType.JSON,
      ...(token ? { [HeaderKeys.AUTHORIZATION]: `Bearer ${token}` } : {}),
      [HeaderKeys.ACCEPT]: MimeType.EVENT_STREAM,
    });

    const response = await fetch(fullUrl, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal,
    });
    if (response.status === 401 || response.status === 403) {
      await authenticationRedirectHandler(response.status);
    }
    return response;
  }
}

export const DevToolsServerExecutor = new DevToolsServerRequestExecutor<string>(
  ExtendSubdomainService.DEVTOOLS_SERVER,
  {
    authorization: { type: 'Bearer' },
  }
).withInterceptor(authenticationInterceptor);

export const DevToolsServerAuthExecutor = new DevToolsServerRequestExecutor<string>(
  ExtendSubdomainService.DEVTOOLS_SERVER,
  {
    authorization: { type: 'Bearer' },
  }
);

// For devtools-server endpoints whose 403 the caller handles in place (e.g. Prism import, custom agent models)
// instead of redirecting the whole page to the forbidden route. See dtsAuthenticationInterceptor for the paths.
export const DevToolsServerInlineForbiddenExecutor = new DevToolsServerRequestExecutor<string>(
  ExtendSubdomainService.DEVTOOLS_SERVER,
  {
    authorization: { type: 'Bearer' },
  }
).withInterceptor(dtsAuthenticationInterceptor);

// okay if this one silently fails — we don't want metrics to impact the user experience
export const DevToolsServerMetricsExecutor = new DevToolsServerRequestExecutor<string>(
  ExtendSubdomainService.DEVTOOLS_SERVER,
  {
    authorization: { type: 'Bearer' },
  }
);
